import axios from 'axios';

import apiClient from '@/lib/apiClient';
import {
  AdditionalArnInput,
  ApiSubItem,
  LeadRecord,
  LeadStatusValue,
  PaymentMode,
  PayStatus,
  ProductDiscount,
  TcStatus,
} from '@/lib/sales-metrics';
import { ApiError, ApiResponse } from '@/types';
import type { RenewalFrequencyValue } from '@/utils/constants';

export interface LeadsListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  from?: string;
  to?: string;
  state?: string[];
  city?: string[];
  pincode?: string[];
  tcStatus?: string[];
  leadStatus?: string[];
  paymentStatus?: string[];
}

export interface LeadsListResponse {
  rows: LeadRecord[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface CreateLeadPayload {
  // Omitted for NON-ARN clients - the server mints their 'NA…' code.
  arn?: string;
  isNonArn?: boolean;
  firstName: string;
  middleName?: string;
  lastName?: string;
  mobile: string;
  email: string;
  city?: string;
  state?: string;
  pincode?: string;
  // The firm's everyday name (Fix 1). Optional - plenty of individual ARN
  // holders trade under no brand at all.
  //
  // `kydCompliant` / `arnValidTill` are deliberately absent from this payload
  // AND from UpdateLeadPayload: the server accepts them from no request body,
  // only from its own ARN lookup.
  brandName?: string;
  // Where the lead came from (plan.md §16.2, task 9.7). ALWAYS OPTIONAL - a
  // payload that omits it creates exactly the lead it created before these
  // fields existed, which is what keeps the importer and the seeds working.
  // The server constrains it to its LEAD_SOURCES vocabulary and stamps the
  // capture instant itself; a client-supplied timestamp is not accepted, because
  // that instant is what separates an observed source from a back-filled one.
  leadSource?: string;
  leadSourceDetail?: string;
}

export interface UpdateLeadPayload {
  arn?: string;
  // Client-type switch: -> true mints a fresh 'NA…' code server-side; -> false
  // requires a real `arn` in the same payload.
  isNonArn?: boolean;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  mobile?: string;
  email?: string;
  city?: string;
  state?: string;
  pincode?: string;
  // `null` clears a brand name that turned out to be wrong; omitted leaves the
  // stored one alone.
  brandName?: string | null;
  // Back-filling the channel on an existing lead. `null` clears it (and the
  // detail with it). The server deliberately does NOT stamp a capture instant on
  // this path: a value typed months after the fact is a reconstruction, and the
  // missing instant is exactly what marks it as one.
  leadSource?: string | null;
  leadSourceDetail?: string | null;
  // Lead conversion, restricted server-side to 'Lead' | 'Future Client'.
  // Promotion to 'Client' belongs to the Onboard Client action (it runs the T&C /
  // quotation-reference / payment gates), and the server also refuses to change
  // the status of a lead that is already a Client - onboarding is one-way.
  // tcStatus stays server-controlled (the acceptance ledger) and is not accepted
  // by PATCH at all; paymentAmount is recomputed server-side from the cart, so
  // the value sent is advisory (Partial figure).
  leadStatus?: Extract<LeadStatusValue, 'Lead' | 'Future Client'>;
  paymentStatus?: PayStatus;
  paymentMode?: PaymentMode | null;
  paymentAmount?: number;
  // Company bank account label chosen in Step 3 (Fix 5).
  selectedBankLabel?: string | null;
  // Payment transaction number/reference (Step 4, Fix 1). Empty string clears it.
  transactionRef?: string | null;
  // Highest onboarding step reached (1-5) for the current order; the server never
  // moves it backwards. Renewal cadence is no longer here - it is per product,
  // saved through the product-details endpoint (ADR-048).
  stepper?: number;
}

export interface OnboardPayload {
  selectedProducts: string[];
  paymentStatus: PayStatus;
  // Null when nothing was collected - an order settled entirely on Due (ADR-053).
  paymentMode: PaymentMode | null;
  // Amount collected. The server recomputes the payable total, derives the
  // price_at_sale snapshot and applies the payment policy (bug 1), so no
  // per-product money maps are sent from the client.
  paymentAmount: number;
}

// Step 1 (Lead Details) upsert payload - the full form, saved live (ADR-017).
export type LeadDetailsPayload = Record<string, string>;

// Step 2 (Product Details) upsert payload - the full selection plus sparse
// per-product maps, saved live to product_details (ADR-018).
export interface ProductDetailsPayload {
  selectedProducts: string[];
  customPrices?: Record<string, number>;
  productTerms?: Record<string, number>;
  productDiscounts?: Record<string, ProductDiscount>;
  productMultiYearDiscounts?: Record<string, number>;
  // Renewal cadence per product code (ADR-048). Missing codes default to Annual.
  productRenewalFrequencies?: Record<string, RenewalFrequencyValue>;
  // Additional ARNs for the ADD_ARN product (see ADR-020). Reconciled into
  // lead_additional_arns server-side when present.
  additionalArns?: AdditionalArnInput[];
  // Sub-item selections for API bundle products (API_MF_RESEARCH, API_CALCULATOR).
  // Keyed by product code. Omitting leaves existing server-side metadata untouched.
  apiItems?: Record<string, ApiSubItem[]>;
}

// Subset of LeadDetailsForm the server can fill in from an ARN lookup.
export interface ArnLookupResult {
  source: 'database' | 'advisorkhoj' | 'amfi';
  existsInDb?: boolean;
  clientName?: string;
  arnHolderName?: string;
  arnAum?: string;
  primaryNumber?: string;
  secondaryNumber?: string;
  primaryEmail?: string;
  websiteUrl?: string;
  city?: string;
  state?: string;
  pincode?: string;
  address?: string;
  remarks?: string;
  // AMFI compliance facts (Fix 1). The server has already persisted these onto
  // the lead by the time this response arrives; they are echoed back so the
  // wizard can show the new values without a refetch.
  //
  // `undefined` means THIS SOURCE could not say - only the AdvisorKhoj MFAPI
  // carries them, the public AMFI fallback carries neither - so it must not be
  // read as "no" or as "cleared". Callers apply each one only when present.
  kydCompliant?: boolean;
  // 'YYYY-MM-DD'.
  arnValidTill?: string;
}

// Subset of LeadDetailsForm the server can fill in from a GSTIN lookup.
export interface GstLookupResult {
  legalName?: string;
  displayName?: string;
  pincode?: string;
  city?: string;
  state?: string;
  address?: string;
}

// Multi-value filters are sent comma-joined; the API splits them back out.
const toParams = (params: LeadsListParams): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  const csv: (keyof LeadsListParams)[] = [
    'state',
    'city',
    'pincode',
    'tcStatus',
    'leadStatus',
    'paymentStatus',
  ];
  (Object.keys(params) as (keyof LeadsListParams)[]).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === '') return;
    if (csv.includes(key) && Array.isArray(value)) {
      if (value.length) out[key] = value.join(',');
    } else if (!Array.isArray(value)) {
      out[key] = value;
    }
  });
  return out;
};

export type TcType = 'ALL' | 'MFTOOLS' | 'API';
export type TcItemStatus = 'accepted' | 'sent' | 'pending' | 'not_required';

export interface TcStatusItem {
  type: TcType;
  status: TcItemStatus;
  acceptedAt: string | null;
}

export interface LeadTcStatus {
  required: TcType[];
  items: TcStatusItem[];
  // Server-derived overall status; the client renders this directly rather than
  // re-deriving the rule (kept in sync with the persisted leads.tc_status).
  overallStatus: TcStatus;
}

export interface LeadTcLink {
  type: TcType;
  title: string;
  url: string;
}

export interface ArnExistsResult {
  exists: boolean;
  ownedByMe: boolean;
}

// One row in the payment schedule (ADR-035).
// amount/dueDate are set in Payment Plan (Step 4); transactionNo/mode are
// filled in Billing Handover (Step 5) when the RM collects payment per row.
export interface PaymentInstallmentRow {
  installmentNumber: number;
  // Due amount in whole rupees.
  amount: number;
  // ISO date string YYYY-MM-DD.
  dueDate: string;
  transactionNo?: string | null;
  mode?: string | null;
  // Set (ISO timestamp) once handed over to billing; locks the row from edits.
  // Server-authoritative and ignored on write - round-tripped only.
  handedOverAt?: string | null;
}

export const leadsService = {
  list: async (params: LeadsListParams = {}): Promise<LeadsListResponse> => {
    const { data } = await apiClient.get<ApiResponse<LeadRecord[]>>('/leads', {
      params: toParams(params),
    });
    return {
      rows: data.data ?? [],
      total: (data.meta?.total as number) ?? 0,
      totalPages: (data.meta?.totalPages as number) ?? 1,
      currentPage: (data.meta?.currentPage as number) ?? 1,
    };
  },

  /**
   * The lead, described from the point of view of one order (ADR-048). Pass
   * `orderId` to open a specific purchase - omitted, the server falls back to
   * the client's open order, else their most recent.
   */
  getById: async (id: number, orderId?: number | null): Promise<LeadRecord> => {
    const { data } = await apiClient.get<ApiResponse<LeadRecord>>(`/leads/${id}`, {
      params: orderId != null ? { orderId } : undefined,
    });
    if (!data.data) throw new Error('Lead not found');
    return data.data;
  },

  getTcStatus: async (id: number): Promise<LeadTcStatus> => {
    const { data } = await apiClient.get<ApiResponse<LeadTcStatus>>(`/leads/${id}/tc-status`);
    if (!data.data) throw new Error('T&C status not found');
    return data.data;
  },

  getTcLinks: async (id: number): Promise<LeadTcLink[]> => {
    const { data } = await apiClient.get<ApiResponse<LeadTcLink[]>>(`/leads/${id}/tc-links`);
    return data.data ?? [];
  },

  checkArn: async (arn: string): Promise<ArnExistsResult> => {
    const { data } = await apiClient.get<ApiResponse<ArnExistsResult>>(`/leads/arn-check/${arn}`);
    return data.data ?? { exists: false, ownedByMe: false };
  },

  create: async (payload: CreateLeadPayload): Promise<LeadRecord> => {
    const { data } = await apiClient.post<ApiResponse<LeadRecord>>('/leads', payload);
    if (!data.data) throw new Error('Failed to create lead');
    return data.data;
  },

  import: async (leads: CreateLeadPayload[]): Promise<{ created: number; skipped: number }> => {
    // A batch can be up to 1000 rows and each missing-pincode row triggers an ARN
    // lookup server-side, so this far outlasts the default timeout. A client abort
    // here is now safe to retry: duplicate ARNs are skipped server-side (the DB
    // enforces one lead per ARN), so a retry re-imports only the rows that landed.
    const { data } = await apiClient.post<ApiResponse<{ created: number; skipped: number }>>(
      '/leads/import',
      { leads },
      { timeout: 120_000 },
    );
    return data.data ?? { created: 0, skipped: 0 };
  },

  update: async (id: number, payload: UpdateLeadPayload): Promise<LeadRecord> => {
    const { data } = await apiClient.patch<ApiResponse<LeadRecord>>(`/leads/${id}`, payload);
    if (!data.data) throw new Error('Failed to update lead');
    return data.data;
  },

  onboard: async (id: number, payload: OnboardPayload): Promise<LeadRecord> => {
    const { data } = await apiClient.post<ApiResponse<LeadRecord>>(`/leads/${id}/onboard`, payload);
    if (!data.data) throw new Error('Failed to onboard lead');
    return data.data;
  },

  saveDetails: async (id: number, payload: LeadDetailsPayload): Promise<LeadRecord> => {
    const { data } = await apiClient.put<ApiResponse<LeadRecord>>(`/leads/${id}/details`, payload);
    if (!data.data) throw new Error('Failed to save lead details');
    return data.data;
  },

  saveProductDetails: async (id: number, payload: ProductDetailsPayload): Promise<LeadRecord> => {
    const { data } = await apiClient.put<ApiResponse<LeadRecord>>(
      `/leads/${id}/product-details`,
      payload,
    );
    if (!data.data) throw new Error('Failed to save product details');
    return data.data;
  },

  lookupArn: async (arn: string, excludeLeadId?: number): Promise<ArnLookupResult> => {
    try {
      const { data } = await apiClient.get<ApiResponse<ArnLookupResult>>(
        `/leads/arn-lookup/${arn}`,
        {
          params: excludeLeadId ? { excludeLeadId } : undefined,
        },
      );
      if (!data.data) throw new Error('ARN lookup returned no data');
      return data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiError = error.response?.data as ApiError | undefined;
        throw new Error(apiError?.message ?? 'ARN lookup failed');
      }
      throw error;
    }
  },

  lookupGst: async (gstin: string): Promise<GstLookupResult> => {
    try {
      const { data } = await apiClient.get<ApiResponse<GstLookupResult>>(
        `/leads/gst-lookup/${gstin}`,
      );
      if (!data.data) throw new Error('GST lookup returned no data');
      return data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiError = error.response?.data as ApiError | undefined;
        throw new Error(apiError?.message ?? 'GST lookup failed');
      }
      throw error;
    }
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/leads/${id}`);
  },

  // `orderId` names the purchase being worked. Without it the server falls back
  // to the client's CURRENT order, which is wrong the moment an RM reopens a
  // confirmed order to settle a due installment while a newer one is open
  // (ADR-053) - the wizard would show, and save over, the wrong schedule.
  getPaymentInstallments: async (
    id: number,
    orderId?: number | null,
  ): Promise<PaymentInstallmentRow[]> => {
    const { data } = await apiClient.get<ApiResponse<PaymentInstallmentRow[]>>(
      `/leads/${id}/payment-installments`,
      { params: orderId != null ? { orderId } : undefined },
    );
    return data.data ?? [];
  },

  savePaymentInstallments: async (
    id: number,
    installments: PaymentInstallmentRow[],
    orderId?: number | null,
  ): Promise<PaymentInstallmentRow[]> => {
    const { data } = await apiClient.put<ApiResponse<PaymentInstallmentRow[]>>(
      `/leads/${id}/payment-installments`,
      { installments },
      { params: orderId != null ? { orderId } : undefined },
    );
    return data.data ?? [];
  },
};
